import { useParams, Navigate, Link } from "react-router-dom";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    updated: "Updated February 2026",
    intro:
      "At Flownté Clothing, we treat your privacy the same way we treat our fabrics — with care, respect, and quiet consideration.",
    sections: [
      {
        h: "Information we collect",
        p: "We collect information you provide directly — your name, email, shipping address, and payment details — when you place an order or subscribe to our newsletter. We also collect non-identifying analytics data to improve your experience.",
      },
      {
        h: "How we use your information",
        p: "Your information is used strictly to process orders, send shipping updates, respond to your questions, and (with your consent) share occasional news from our atelier. We never sell your data to third parties.",
      },
      {
        h: "Cookies",
        p: "We use minimal first-party cookies to keep your cart populated and remember your preferences. You may disable cookies in your browser at any time.",
      },
      {
        h: "Your rights",
        p: "You may request access, correction, or deletion of your personal information at any time by emailing hello@flownte.in. We respond within 7 working days.",
      },
      {
        h: "Contact",
        p: "Questions? Write to hello@flownte.in or to our atelier in Guwahati, Assam, India.",
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Updated February 2026",
    intro:
      "By shopping with Flownté Clothing, you agree to the following soft but firm terms. They exist to keep things graceful for both of us.",
    sections: [
      {
        h: "Orders",
        p: "All orders are subject to availability and confirmation of the order price. An order is only deemed accepted once payment has been successfully processed and a confirmation email has been sent.",
      },
      {
        h: "Pricing",
        p: "Prices are listed in Indian Rupees (INR) and are inclusive of applicable taxes where stated. Shipping charges, if any, are calculated at checkout.",
      },
      {
        h: "Intellectual property",
        p: "All content on flownte.in — including imagery, illustrations, designs, and text — is the property of Flownté Clothing and may not be reproduced without written permission.",
      },
      {
        h: "Limitation of liability",
        p: "Flownté Clothing shall not be liable for any indirect, incidental, or consequential damages arising from the use of our products or website.",
      },
      {
        h: "Governing law",
        p: "These terms are governed by the laws of India, and any disputes are subject to the exclusive jurisdiction of the courts of Guwahati, Assam.",
      },
    ],
  },
  refund: {
    title: "Refund & Return Policy",
    updated: "Updated February 2026",
    intro:
      "We want you to love every Flownté piece. If something doesn't feel right, we'll make it right — gently.",
    sections: [
      {
        h: "Return window",
        p: "You may request a return within 7 days of receiving your order. The piece must be unworn, unwashed, and returned with its original tags and packaging.",
      },
      {
        h: "How to initiate a return",
        p: "Write to hello@flownte.in with your order number and a short note about why you're returning. We'll arrange a pickup from your address within 3 working days.",
      },
      {
        h: "Refunds",
        p: "Refunds are processed within 5 – 7 working days after we receive and inspect the returned piece. The amount is credited to your original payment method.",
      },
      {
        h: "Exchanges",
        p: "We offer size exchanges on most pieces, subject to availability. Festive or limited-edition pieces are eligible for exchange or store credit only.",
      },
      {
        h: "Non-returnable",
        p: "Pieces marked Final Sale, customised/made-to-measure orders, and innerwear are not eligible for returns.",
      },
    ],
  },
};

const LINKS = [
  { to: "/legal/privacy", label: "Privacy Policy" },
  { to: "/legal/terms", label: "Terms & Conditions" },
  { to: "/legal/refund", label: "Refund & Returns" },
];

export default function Legal() {
  const { slug } = useParams();
  const content = CONTENT[slug];

  if (!content) return <Navigate to="/legal/privacy" replace />;

  return (
    <div data-testid={`legal-page-${slug}`} className="pb-24">
      <section className="relative py-16 md:py-20 bg-gradient-to-b from-blush-50 via-cream-100 to-cream-100 border-b border-blush-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="font-script text-3xl text-blush-400">the fine print</span>
          <h1 className="font-heading text-4xl md:text-5xl text-ink-900 mt-1">
            {content.title}
          </h1>
          <p className="mt-3 font-body text-xs uppercase tracking-[0.22em] text-ink-500">
            {content.updated}
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-3">
          <div className="sticky top-28 space-y-2">
            <p className="text-xs uppercase tracking-[0.22em] text-ink-500 font-body mb-3">
              Documents
            </p>
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`block font-body text-sm transition ${
                  l.to === `/legal/${slug}`
                    ? "text-blush-600 font-medium"
                    : "text-ink-700 hover:text-blush-600"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </aside>

        <article className="lg:col-span-9 space-y-10">
          <p className="font-body text-lg text-ink-700 leading-relaxed">
            {content.intro}
          </p>
          {content.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-heading text-2xl md:text-3xl text-ink-900">{s.h}</h2>
              <p className="mt-3 font-body text-ink-700 leading-relaxed">{s.p}</p>
            </section>
          ))}
        </article>
      </section>
    </div>
  );
}
